import { useEffect, useMemo, useState } from 'react';
import type { FieldPath, UseFormReturn } from 'react-hook-form';
import { getCalculatedCommission } from './commission';

interface WithdrawAmountFields {
  amount?: string;
  includeFee?: boolean;
}

interface UseWithdrawTotalAmountOptions<
  TFormValues extends WithdrawAmountFields,
  TWatchedValues extends WithdrawAmountFields = TFormValues,
> {
  form: UseFormReturn<TFormValues>;
  watchedValues: TWatchedValues;
  commissionData?: {
    feeAmount: number;
    amountWithFee?: number;
  };
  assetCode?: string;
  precision?: number;
  shouldTriggerValidation?: (watchedValues: TWatchedValues) => boolean;
  isCommissionLoading?: boolean;
  additionalDependencies?: unknown[];
  validationDependencies?: unknown[];
}

interface UseWithdrawTotalAmountReturn {
  totalAmount: number;
}

export const useWithdrawTotalAmount = <
  TFormValues extends WithdrawAmountFields,
  TWatchedValues extends WithdrawAmountFields = TFormValues,
>({
  form,
  watchedValues,
  commissionData,
  assetCode,
  precision,
  shouldTriggerValidation = values => !!values.amount,
  isCommissionLoading,
  additionalDependencies = [],
  validationDependencies,
}: UseWithdrawTotalAmountOptions<
  TFormValues,
  TWatchedValues
>): UseWithdrawTotalAmountReturn => {
  const [totalAmount, setTotalAmount] = useState(0);
  const validationDeps = useMemo(
    () => validationDependencies ?? [watchedValues.amount],
    [validationDependencies, watchedValues.amount]
  );
  const shouldValidate = shouldTriggerValidation(watchedValues);

  useEffect(() => {
    const { totalWithdrawAmount } = getCalculatedCommission({
      amount: watchedValues.includeFee
        ? commissionData?.amountWithFee || 0
        : Number(watchedValues?.amount) || 0,
      includeFee: !!watchedValues.includeFee,
      fee: commissionData?.feeAmount || 0,
      assetCode,
      precision,
    });

    setTotalAmount(totalWithdrawAmount);
  }, [
    watchedValues.includeFee,
    watchedValues.amount,
    commissionData?.amountWithFee,
    commissionData?.feeAmount,
    assetCode,
    precision,
    isCommissionLoading,
    ...additionalDependencies,
  ]);

  useEffect(() => {
    if (!shouldValidate) {
      return;
    }

    form.trigger('amount' as FieldPath<TFormValues>);
  }, [totalAmount, form, shouldValidate, ...validationDeps]);

  return {
    totalAmount,
  };
};
